<template>
  <b-card class="supplements-settings">
    <div class="supplements-settings__heading">
      <div>
        <h2 class="supplements-settings__title">
          {{ $t('settings.sections.supplements.title') }}
        </h2>
        <p class="text-muted mb-0">
          {{ $t('settings.sections.supplements.description') }}
        </p>
      </div>
      <b-button
        variant="primary"
        @click="openCreateModal">
        <v-icon name="plus" />
        {{ $t('settings.sections.supplements.add') }}
      </b-button>
    </div>

    <b-alert
      :show="Boolean(loadError)"
      variant="danger">
      {{ loadError }}
    </b-alert>

    <b-form-row class="supplements-settings__filters">
      <b-col md="5">
        <b-form-input
          v-model.trim="search"
          :placeholder="$t('settings.sections.supplements.search')"
          type="search" />
      </b-col>
      <b-col md="4">
        <b-form-radio-group
          v-model="typeFilter"
          :options="typeFilterOptions"
          buttons
          button-variant="outline-secondary" />
      </b-col>
      <b-col
        md="3"
        class="pt-2">
        <b-form-checkbox v-model="showInactive">
          {{ $t('settings.sections.supplements.showInactive') }}
        </b-form-checkbox>
      </b-col>
    </b-form-row>

    <div
      v-if="loading"
      class="text-center p-4">
      <b-spinner small />
      {{ $t('settings.loading') }}
    </div>

    <div
      v-else-if="!visibleSupplements.length"
      class="text-center text-muted p-4">
      {{ $t('settings.sections.supplements.empty') }}
    </div>

    <b-table
      v-else
      :items="visibleSupplements"
      :fields="fields"
      :tbody-tr-class="rowClass"
      responsive
      hover
      small>
      <template #cell(type)="data">
        <b-badge :variant="data.item.type.name === 'ANALYSIS' ? 'info' : 'secondary'">
          {{ $t(`settings.sections.supplements.types.${data.item.type.name}`) }}
        </b-badge>
      </template>
      <template #cell(price)="data">
        {{ formatPrice(data.item.price) }}
      </template>
      <template #cell(units)="data">
        {{ (data.item.units || []).join(', ') }}
      </template>
      <template #cell(active)="data">
        <b-badge :variant="data.item.active ? 'success' : 'secondary'">
          {{ data.item.active ? $t('settings.sections.supplements.active') : $t('settings.sections.supplements.inactive') }}
        </b-badge>
      </template>
      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="outline-primary"
          :aria-label="$t('settings.sections.supplements.edit')"
          :disabled="busyId === data.item.id"
          @click="openEditModal(data.item)">
          <v-icon name="edit" />
        </b-button>
        <b-button
          v-if="data.item.active"
          size="sm"
          variant="outline-danger"
          class="ml-1"
          :aria-label="$t('settings.sections.supplements.deactivate')"
          :disabled="busyId === data.item.id"
          @click="confirmDeactivate(data.item)">
          <v-icon name="ban" />
        </b-button>
        <b-button
          v-else
          size="sm"
          variant="outline-success"
          class="ml-1"
          :aria-label="$t('settings.sections.supplements.reactivate')"
          :disabled="busyId === data.item.id"
          @click="reactivate(data.item)">
          <v-icon name="undo" />
        </b-button>
      </template>
    </b-table>

    <b-alert
      :show="Boolean(actionError)"
      variant="danger"
      class="mt-3">
      {{ actionError }}
    </b-alert>

    <b-modal
      ref="editorModal"
      size="xl"
      :title="editor.id ? $t('settings.sections.supplements.edit') : $t('settings.sections.supplements.add')"
      :ok-title="$t('settings.save')"
      :cancel-title="$t('settings.cancel')"
      :ok-disabled="saving"
      @ok="save">
      <b-form @submit.stop.prevent="save">
        <b-alert
          :show="Boolean(editorError)"
          variant="danger">
          {{ editorError }}
        </b-alert>

        <b-form-row>
          <b-col md="4">
            <b-form-group
              :label="$t('settings.sections.supplements.type')"
              label-for="supplement-type">
              <b-form-select
                id="supplement-type"
                v-model="editor.type"
                :options="typeOptions"
                :disabled="Boolean(editor.id)" />
            </b-form-group>
          </b-col>
          <b-col md="8">
            <b-form-group
              :label="$t('settings.sections.supplements.internalRef')"
              :description="refDescription"
              :invalid-feedback="$t('settings.sections.supplements.internalRefRequired')"
              :state="state('internalRef')"
              label-for="supplement-ref">
              <b-form-input
                id="supplement-ref"
                v-model.trim="editor.internalRef"
                :state="state('internalRef')"
                :disabled="Boolean(editor.id)"
                maxlength="100" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-group
          :label="$t('settings.sections.supplements.name')"
          :invalid-feedback="$t('settings.sections.supplements.nameRequired')"
          :state="state('name')"
          label-for="supplement-name">
          <b-form-input
            id="supplement-name"
            v-model.trim="editor.name"
            :state="state('name')"
            maxlength="255" />
        </b-form-group>

        <b-form-group
          :label="$t('settings.sections.supplements.descriptionLabel')"
          label-for="supplement-description">
          <b-form-textarea
            id="supplement-description"
            v-model="editor.description"
            maxlength="255"
            rows="2" />
        </b-form-group>

        <b-form-row>
          <b-col md="4">
            <b-form-group
              :label="$t('settings.sections.supplements.price')"
              :description="$t('settings.sections.supplements.priceHelp')"
              :invalid-feedback="$t('settings.sections.supplements.priceInvalid')"
              :state="state('price')"
              label-for="supplement-price">
              <b-form-input
                id="supplement-price"
                v-model.number="editor.price"
                :state="state('price')"
                type="number"
                min="0"
                step="0.01" />
            </b-form-group>
          </b-col>
          <b-col md="8">
            <b-form-group
              :label="$t('settings.sections.supplements.units')"
              :invalid-feedback="$t('settings.sections.supplements.unitsRequired')"
              :state="state('units')">
              <b-form-checkbox-group
                v-model="editor.units"
                :options="unitOptions"
                :state="state('units')"
                stacked />
            </b-form-group>
          </b-col>
        </b-form-row>
      </b-form>
    </b-modal>
  </b-card>
</template>

<script>
import {
  createSupplement,
  deactivateSupplement,
  findAllSupplements,
  findSupplementUnits,
  reactivateSupplement,
  updateSupplement
} from '../../../services/supplementService';

const MANAGED_TYPES = [ 'OPERATION', 'ANALYSIS' ];

const emptyEditor = () => ({
  id: null,
  type: 'OPERATION',
  internalRef: '',
  name: '',
  description: '',
  price: 0,
  units: []
});

export default {
  data() {
    return {
      supplements: [],
      unitOptions: [],
      search: '',
      typeFilter: 'ALL',
      showInactive: false,
      loading: false,
      loadError: '',
      actionError: '',
      busyId: null,
      editor: emptyEditor(),
      editorError: '',
      saving: false,
      submitted: false
    };
  },
  computed: {
    fields() {
      return [
        { key: 'type', label: this.$t('settings.sections.supplements.type') },
        { key: 'internalRef', label: this.$t('settings.sections.supplements.internalRef') },
        { key: 'name', label: this.$t('settings.sections.supplements.name') },
        { key: 'price', label: this.$t('settings.sections.supplements.price'), class: 'text-right' },
        { key: 'units', label: this.$t('settings.sections.supplements.units') },
        { key: 'active', label: this.$t('settings.sections.supplements.status') },
        { key: 'actions', label: '', class: 'text-nowrap text-right' }
      ];
    },
    typeFilterOptions() {
      return [ 'ALL', ...MANAGED_TYPES ].map(value => ({
        value,
        text: this.$t(`settings.sections.supplements.types.${value}`)
      }));
    },
    typeOptions() {
      return MANAGED_TYPES.map(value => ({
        value,
        text: this.$t(`settings.sections.supplements.types.${value}`)
      }));
    },
    visibleSupplements() {
      const search = this.search.toLowerCase();

      return this.supplements
        .filter(supplement => MANAGED_TYPES.includes(supplement.type && supplement.type.name))
        .filter(supplement => this.showInactive || supplement.active)
        .filter(supplement => this.typeFilter === 'ALL' || supplement.type.name === this.typeFilter)
        .filter(supplement => !search
          || (supplement.name || '').toLowerCase().includes(search)
          || (supplement.internalRef || '').toLowerCase().includes(search))
        .sort((a, b) => a.type.name.localeCompare(b.type.name) || (a.name || '').localeCompare(b.name || ''));
    },
    refDescription() {
      if (this.editor.id) return this.$t('settings.sections.supplements.internalRefLocked');

      return this.editor.type === 'ANALYSIS'
        ? this.$t('settings.sections.supplements.internalRefAnalysisHelp')
        : this.$t('settings.sections.supplements.internalRefOptional');
    }
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.loadError = '';
      Promise.all([ findAllSupplements(), findSupplementUnits() ])
        .then(([ supplements, units ]) => {
          this.supplements = Array.isArray(supplements) ? supplements : [];
          this.unitOptions = units;
        })
        .catch(() => {
          this.loadError = this.$t('settings.sections.supplements.loadError');
        })
        .then(() => {
          this.loading = false;
        });
    },
    rowClass(item) {
      return item && !item.active ? 'text-muted supplements-settings__inactive' : '';
    },
    formatPrice(price) {
      return price === null || price === undefined
        ? '-'
        : new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'EUR' }).format(price);
    },
    openCreateModal() {
      this.editor = emptyEditor();
      this.editorError = '';
      this.submitted = false;
      this.$refs.editorModal.show();
    },
    openEditModal(supplement) {
      this.editor = {
        id: supplement.id,
        type: supplement.type.name,
        internalRef: supplement.internalRef || '',
        name: supplement.name || '',
        description: supplement.description || '',
        price: supplement.price,
        units: [ ...(supplement.units || []) ]
      };
      this.editorError = '';
      this.submitted = false;
      this.$refs.editorModal.show();
    },
    state(field) {
      if (!this.submitted) return null;
      switch (field) {
        case 'internalRef':
          return this.editor.id || this.editor.type !== 'ANALYSIS' ? null : Boolean(this.editor.internalRef);
        case 'price':
          return typeof this.editor.price === 'number' && this.editor.price >= 0;
        case 'units':
          return this.editor.units.length > 0;
        default:
          return Boolean(this.editor[field]);
      }
    },
    isValid() {
      return [ 'internalRef', 'name', 'price', 'units' ].every(field => this.state(field) !== false);
    },
    save(event) {
      if (event && event.preventDefault) event.preventDefault();
      this.submitted = true;
      if (!this.isValid()) return;

      this.saving = true;
      this.editorError = '';
      const payload = {
        name: this.editor.name,
        description: this.editor.description || null,
        price: this.editor.price,
        units: this.editor.units
      };
      const request = this.editor.id
        ? updateSupplement(this.editor.id, payload)
        : createSupplement({ ...payload, type: this.editor.type, internalRef: this.editor.internalRef || null });

      request
        .then(() => {
          this.$refs.editorModal.hide();
          this.load();
        })
        .catch(error => {
          this.editorError = this.errorMessage(error, 'saveError');
        })
        .then(() => {
          this.saving = false;
        });
    },
    confirmDeactivate(supplement) {
      this.$bvModal.msgBoxConfirm(this.$t('settings.sections.supplements.deactivateConfirmation', { name: supplement.name }), {
        title: this.$t('settings.sections.supplements.deactivate'),
        okTitle: this.$t('settings.sections.supplements.deactivate'),
        okVariant: 'danger',
        cancelTitle: this.$t('settings.cancel'),
        centered: true
      }).then(confirmed => {
        if (confirmed) this.runAction(supplement, deactivateSupplement, 'deactivateError');
      });
    },
    reactivate(supplement) {
      this.runAction(supplement, reactivateSupplement, 'reactivateError');
    },
    runAction(supplement, action, errorKey) {
      this.busyId = supplement.id;
      this.actionError = '';
      action(supplement.id)
        .then(() => this.load())
        .catch(error => {
          this.actionError = this.errorMessage(error, errorKey);
        })
        .then(() => {
          this.busyId = null;
        });
    },
    errorMessage(error, fallbackKey) {
      const status = error && error.response && error.response.status;
      if (status === 409) return this.$t('settings.sections.supplements.duplicateRef');
      if (status === 403) return this.$t('settings.sections.supplements.forbidden');

      return this.$t(`settings.sections.supplements.${fallbackKey}`);
    }
  }
};
</script>

<style scoped>
.supplements-settings {
  text-align: left;
}

.supplements-settings__heading {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.supplements-settings__title {
  font-size: 1.25rem;
}

.supplements-settings__filters {
  margin-bottom: 1rem;
}

@media (max-width: 575px) {
  .supplements-settings__heading {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
